/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { format } from 'date-fns';
import { httpClient } from '../services/http/httpClient';
export function convertToPath(name) {
    var path = name.toLowerCase();
    return path.replace(/\s+/g, '-');
}
export function formatDate(text) {
    try {
        var date = new Date(text);
        return format(date, 'dd/MM/yyyy HH:mm');
    }
    catch (_a) {
        return text;
    }
}
export function stopPropagation(e) {
    e.stopPropagation();
}
export function isPropDefined(prop) {
    return prop !== undefined;
}
export function falseIfUndefined(prop) {
    if (prop) {
        return true;
    }
    return false;
}
export function isEmptyObject(x) {
    return Object.keys(x).length === 0;
}
export function normaliseCaps(name, endpoint) {
    if (name === undefined)
        return "";
    // make object ids clear (for auto load)
    if (endpoint !== undefined) {
        if (name === "id" || name === "uid") {
            return normaliseCaps(endpoint) + " ID";
        }
    }
    // replace relationship '.' with underscore ready to split
    name = name.replace('.', '_');
    var words = name.split('_');
    for (var count = 0; count < words.length; count++) {
        words[count] = normaliseWords(words[count]);
    }
    return words.join(' ');
}
function normaliseWords(word) {
    switch (word) {
        case "uid":
        case "id":
            return "ID";
        case "sts":
            return "STS";
        case "tolqc":
            return "ToLQC";
        case "tolid":
            return "ToLID";
        case "tol":
            return "ToL";
        case "eln":
            return "ELN";
        case "dna":
            return "DNA";
        case "rna":
            return "RNA";
        case "mlwh":
            return "MLWH";
        case "api":
            return "API";
        case "gal":
            return "GAL";
        case "qc":
            return "QC";
        case "bnt":
            return "BnT";
        case "tubeid":
            return "Tube ID";
        default:
            return word[0].toUpperCase() + word.substring(1);
    }
}
export function getCssVarValue(variable) {
    return getComputedStyle(document.documentElement).getPropertyValue(variable);
}
export function timeout(delay) {
    return new Promise(function (res) { return setTimeout(res, delay); });
}
export function matomoAnalytics(siteId) {
    if (siteId) {
        var _paq_1 = window["_paq"] = window["_paq"] || [];
        // tracker methods like "setCustomDimension" should be called before "trackPageView"
        _paq_1.push(['trackPageView']);
        _paq_1.push(['enableLinkTracking']);
        (function () {
            var u = "https://matomo.sanger.ac.uk/";
            _paq_1.push(['setTrackerUrl', u + 'matomo.php']);
            _paq_1.push(['setSiteId', siteId]);
            var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
            g.async = true;
            g.src = u + 'matomo.js';
            s.parentNode.insertBefore(g, s);
        })();
    }
}
export function getConfig(endpoint, baseUrl) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, httpClient().get('/_config/' + endpoint, {
                        baseURL: baseUrl
                    }).then(function (res) {
                        return res.data;
                    }).catch(function (error) {
                        throw error;
                    })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
